import { getApiUrl, getCurrentLanguageId, hide } from "./common";
import HttpClient from "./http-client";
import $ from "jquery";
import '@selectize/selectize'


const wrapperClass = 'region-section__select-wrapper';

export default class DeliveryRegionComponent {
    private baseElement: HTMLElement;
    private selectContainer: HTMLElement;
    private regions: DeliveryRegion[];
    private selectedRegion: DeliveryRegion;
    private selects: JQuery[];
    private onChange: (region: DeliveryRegion) => void;

    constructor(onChange?: (region: DeliveryRegion) => void) {
        this.baseElement = document.querySelector('[data-component="DeliveryRegion"]');
        this.selectContainer = document.querySelector('[data-component-part="SelectContainer"]');
        this.onChange = onChange;
        this.init();
    }

    private hide(select: JQuery) {
        select?.parent().hide();
    }

    private show(select: JQuery) {
        select?.parent().show();
    }

    private async init(): Promise<void> {
        await this.loadDeliveryRegions();
        const regionId = Number(this.baseElement.dataset.activeRegion);
        this.selects = [];
        this.selectedRegion = regionId
            ? this.getRegion(regionId) ?? this.getDefaultRegion()
            : this.getDefaultRegion();
        if (!regionId && this.selectedRegion) {
            await this.updateDeliveryRegion(this.selectedRegion.regionId);
        }

        this.updateUI();
        this.onChange && this.onChange(this.selectedRegion);
    }

    private updateUI(): void {
        const region = this.selectedRegion;
        const regions = this.regions;
        const path = this.getRegionPath(region);
        path
            .map((e, i) => { return { region: e, siblings: i > 0 ? path[i - 1].children : regions } })
            .forEach((e, i) => {
                const select = this.getSelectElement(i);
                const value = select[0].selectize.items.length === 1 ? Number(select[0].selectize.items[0]) : null;
                if (value !== e.region.regionId) {
                    this.setValues(select, e.siblings);
                    select[0].selectize.clear(true);
                    select[0].selectize.addItem(e.region.regionId, true);
                    this.show(select);
                }
            });

        if (path.length !== this.selects.length) {
            for (let index = path.length + 1; index <= this.selects.length; index++) {
                const select = this.selects[index];
                this.hide(select);
            }
        }

        const currentSelectionLevel = region?.children ?? regions;
        if (currentSelectionLevel.length !== 0) {
            const select = this.getSelectElement(path.length);
            this.setValues(select, currentSelectionLevel);
            this.show(select);
        }
        else if (path.length < this.selects.length) {
            const select = this.getSelectElement(path.length);
            this.setValues(select, currentSelectionLevel);
            this.hide(select);
        }
    }

    private async valueChanged(regionId: number): Promise<void> {
        const region = this.getRegion(regionId);
        const changeDetected = region?.regionId !== this.selectedRegion?.regionId;
        this.selectedRegion = region ? region : null;

        this.updateUI();
        changeDetected && await this.onChangeInternal(region?.hasChildren ? null : region);
    }

    private async onChangeInternal(region: DeliveryRegion): Promise<void> {
        await this.updateDeliveryRegion(region?.regionId);
        this.baseElement.dataset.activeRegion = region?.regionId.toString();
        this.onChange && this.onChange(region);
    }

    private async updateDeliveryRegion(regionId: number): Promise<void> {
        const url = `${getApiUrl()}/cart/delivery-region`;
        const httpClient = new HttpClient();
        await httpClient.fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Current-Language-Id': getCurrentLanguageId()
            },
            body: JSON.stringify({ regionId: regionId ?? null })
        });
    }

    private getOptions(selectize: JQuery): DeliveryRegion[] {
        const options = selectize[0].selectize.options;
        if (!options) return [];
        const result = [];
        for (const option in options) {
            result.push(options[option]);
        }
        return result;
    }

    private setValues(selectize: JQuery, regions: DeliveryRegion[]) {
        const options = this.getOptions(selectize);

        const ids = options.map(o => Number(o.regionId));
        const newIds = regions.map(r => r.regionId);
        if (newIds.length === 0) {
            this.clearOptions(selectize);
            selectize[0].selectize.refreshOptions(false);
            return;
        }
        if (ids.length !== 0 && this.areSame(ids, newIds)) {
            return;
        }

        this.clearOptions(selectize);
        selectize[0].selectize.addOption(regions.sort((x, y) => x.sortValue - y.sortValue));
        selectize[0].selectize.refreshOptions(false);
    }

    private areSame(array1: number[], array2: number[]): boolean {
        if (!array1 || !array2) return false;
        if (array1.length !== array2.length) return false;

        for (let i = 0; i < array1.length; i++) {
            if (array1[i] !== array2[i]) return false;
        }
    }

    private clearOptions(select: JQuery) {
        select[0].selectize.clear(true);
        select[0].selectize.clearOptions();
    }

    private async loadDeliveryRegions(): Promise<void> {
        const client = new HttpClient();
        const url = `${getApiUrl()}/delivery-region`
        const response = await client.fetch(url, {
            headers: {
                'Current-Language-Id': getCurrentLanguageId()
            }
        });

        if (!response.ok) return;

        this.regions = await response.json() as DeliveryRegion[];
    }

    private getSelectElement(index: number): JQuery | null {
        const select = index in this.selects ? this.selects[index] : null;
        if (select) return select;
        const newSelect = document.createElement('select');
        hide(newSelect);
        const div = document.createElement('div');
        div.classList.add(wrapperClass);
        div.dataset.testid = 'checkout-region-level';
        div.appendChild(newSelect);
        this.selectContainer.appendChild(div);
        const selectize = $(newSelect).selectize({
            labelField: 'name',
            valueField: 'regionId',
            onChange: (id) => this.valueChanged(Number(id))
        });

        this.selects[index] = selectize;
        this.hide(selectize);
        return selectize;
    }

    private getParentRegion(region: DeliveryRegion) {
        const regionId = region.regionId;
        const predicate = (r: DeliveryRegion) => r.children && r.children.some(ch => ch.regionId === regionId);
        return this.findRegion(this.regions, predicate);
    }

    private getRegionPath(region: DeliveryRegion): DeliveryRegion[] {
        let currentRegion = region;
        const path: DeliveryRegion[] = [];
        if (!currentRegion) return path;
        do {
            path.push(currentRegion);
            currentRegion = this.getParentRegion(currentRegion);
        } while (currentRegion)
        return path.reverse();
    }

    private getRegion(regionId: number): DeliveryRegion {
        return this.findRegion(this.regions, (r: DeliveryRegion) => r.regionId === regionId);
    }

    private getDefaultRegion(): DeliveryRegion {
        return this.findRegion(this.regions, (r: DeliveryRegion) => r.isDefault);
    }

    private findRegion(regions: DeliveryRegion[], predicate: (r: DeliveryRegion) => boolean): DeliveryRegion {
        const result = regions.find((v) => predicate(v));
        if (result) return result;
        for (let index = 0; index < regions.length; index++) {
            const r = regions[index];
            if (r.children) {
                const childResult = this.findRegion(r.children, predicate);
                if (childResult) return childResult;
            }
        }
        return null;
    }
}

export interface DeliveryRegion {
    regionId: number,
    name: string,
    children: DeliveryRegion[],
    hasChildren: boolean,
    relatedDeliveryOptions: number[],
    isDefault: boolean,
    sortValue: number,
    vatValidation: boolean
}

